import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import InteractiveBundle from './InteractiveBundle';
import { POSTERS, STICKERS } from '../constants';

const HeroSection: React.FC = () => {
  const words = ["Laptop", "Bike", "Workspace", "Console", "Reality"];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, 2500); 
    return () => clearInterval(interval);
  }, [words.length]);

  // Prepare data for the bundles
  const [topPosters, setTopPosters] = useState(POSTERS.slice(0, 15));

  useEffect(() => {
    // Shuffle posters on mount to randomize the interactive bundle every page load
    const shuffledPosters = [...POSTERS].sort(() => 0.5 - Math.random());
    setTopPosters(shuffledPosters.slice(0, 15));
  }, []);

  return (
    <div className="relative min-h-[100vh] flex flex-col justify-center items-center overflow-hidden bg-neutral-900 pt-24 pb-12 md:py-0">
      
      {/* ── Premium Aesthetic Grid ── */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] md:bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_100%_100%_at_50%_50%,#000_20%,transparent_100%)] pointer-events-none opacity-50"></div>

      {/* ── Dynamic Light Orbs ── */}
      <div className="absolute top-0 right-0 w-[150vw] md:w-[60vw] h-[150vw] md:h-[60vw] bg-amber-600/20 md:bg-amber-600/30 rounded-full blur-[100px] md:blur-[150px] pointer-events-none animate-pulse -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[150vw] md:w-[50vw] h-[150vw] md:h-[50vw] bg-blue-600/20 md:bg-blue-600/20 rounded-full blur-[100px] md:blur-[130px] pointer-events-none translate-y-1/3 -translate-x-1/3" style={{ animation: 'pulse 8s cubic-bezier(0.4, 0, 0.6, 1) infinite reverse' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120vw] h-[120vw] bg-pink-500/10 rounded-full blur-[100px] pointer-events-none md:hidden" style={{ animation: 'pulse 5s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>

      {/* ── Layout Container ── */}
      <div className="z-10 container mx-auto px-4 w-full flex flex-col md:flex-row items-center justify-between gap-12 md:gap-4 relative h-full">
        


        {/* Desktop: Posters Bundle (Hidden on Mobile) */}
        <div className="hidden md:flex flex-1 justify-end perspective-1000 order-1">
           <div className="relative mr-4 lg:mr-8 rotate-[8deg] hover:rotate-[4deg] transition-transform duration-700 hover:scale-105 z-20">
             <InteractiveBundle items={topPosters} exitDirection="left" className="w-[260px] h-[380px] lg:w-[300px] lg:h-[420px]" />
             <div className="absolute -top-6 -left-6 bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full -rotate-12 shadow-[0_0_20px_rgba(147,51,234,0.5)] flex items-center gap-2 z-50">
                <span className="w-2 h-2 rounded-full bg-white animate-ping"></span>
                Tap / Swipe Posters
             </div>
           </div>
        </div>

        {/* Center Text Section */}
        <div className="text-center w-full md:w-auto flex-shrink-0 order-2 flex flex-col items-center z-20">
          <h2 className="text-xs md:text-sm font-medium tracking-[0.5em] text-neutral-500 mb-6 uppercase">
            Est. 2025 • The Curated Lifestyle
          </h2>
          <h1 className="text-[12vw] md:text-7xl lg:text-8xl font-black tracking-tighter mb-4 md:mb-6 leading-none flex flex-col items-center">
            <span className="drop-shadow-2xl text-white">ELEVATE YOUR</span>
            {/* Stable height wrapper to prevent button jumping */}
            <div className="h-[1.2em] relative flex justify-center w-full mt-2">
              <AnimatePresence>
                <motion.span
                  key={words[index]}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="absolute text-amber-500 drop-shadow-[0_0_15px_rgba(245,158,11,0.5)]"
                >
                  {words[index]}
                </motion.span>
              </AnimatePresence>
            </div>
          </h1>
          <a href="#collection" className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-bold tracking-widest shadow-[0_0_40px_rgba(168,85,247,0.3)] hover:shadow-[0_0_60px_rgba(168,85,247,0.5)] hover:bg-white hover:text-black transition-all duration-500 mt-8 hover:scale-105 active:scale-95">
            EXPLORE THE DROP
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Dummy div to balance flex layout and keep text centered */}
        <div className="hidden md:block flex-1 order-3"></div>

      </div>

      <div className="absolute inset-x-0 bottom-6 flex justify-center text-neutral-500 pointer-events-none z-0 hidden md:flex">
        <div className="animate-pulse">
          <span className="text-[10px] uppercase tracking-widest pl-1 font-bold">Scroll Down</span>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
